import React, { useEffect } from "react";
import { useTypedSelector } from "store";
import styled from "styled-components";
import colors from "theme/colors";
import AddNewTableButton from "./AddNewTableButton.component";
import PLFiltersRulesTables from "../../../components/PLFiltersRulesTables.component";
import { LoaderSpinner2 } from "components";
import { ProductDetailPanelTitle } from "../ProductDetailPanelHeader";
import {
  fetchTableIfNeededAction,
  TABLES_ITEMS_PER_PAGE,
} from "store/reducers/products/tables/tables.actions";
import RulesTable from "pages/Products/components/RulesTable/RulesTable.component";
import { selectTablesPagination } from "../../../../../store/reducers/products/tables/tables.selectors";
import Pagination from "../../../../../components/Pagination/Pagination.component";
import ImportTableFromTSVButton from "./ImportTableFromTSVButton.component";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store";

export default function ProductLineRulesTables() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const targetTableId =
    typeof queryParams.tableId === "string" ? queryParams.tableId : undefined;

  const { byId: rulesTablesById } = useTypedSelector(
    (state) => state.products.tables
  );

  const { getting: gettingRulesTables } = useTypedSelector(
    (state) => state.products.tables
  );

  const { total } = useTypedSelector((state) => selectTablesPagination(state));

  const page = parseInt(String(queryParams.page || 1));

  useEffect(() => {
    if (!targetTableId || gettingRulesTables) {
      return;
    }

    void dispatch(fetchTableIfNeededAction(targetTableId));
  }, [dispatch, gettingRulesTables, targetTableId]);

  // Only depend on whether the deep-linked table is present — not the whole
  // byId object. Validation (and other) updates replace byId and must not
  // re-trigger scrollIntoView.
  const isTargetTableLoaded = Boolean(
    targetTableId && rulesTablesById[targetTableId]
  );

  useEffect(() => {
    if (!targetTableId || gettingRulesTables || !isTargetTableLoaded) {
      return;
    }

    requestAnimationFrame(() => {
      document.getElementById(`rules-table-${targetTableId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [gettingRulesTables, isTargetTableLoaded, targetTableId]);

  const scrollToTop = () => {
    const page = document.getElementById("product-lines-wrapper");
    page && page.scrollTo(0, 0);
  };

  return (
    <Wrapper>
      {
        <FilterAddWrapper>
          <PLFiltersRulesTables />
          <AddNewTableButton />
          <ImportTableFromTSVButton />
        </FilterAddWrapper>
      }
      <ProductDetailPanelTitle>Tables</ProductDetailPanelTitle>
      <PropertiesListWrapper>
        {!gettingRulesTables &&
          Object.keys(rulesTablesById).map((id) => (
            <RuletableStyledWrapper key={id} id={`rules-table-${id}`}>
              <RulesTable id={id} ruleTableData={rulesTablesById[id]} />
            </RuletableStyledWrapper>
          ))}
        <LoaderWrapper>
          {gettingRulesTables ? (
            <LoaderSpinner2 />
          ) : (
            <Pagination
              page={page}
              itemsPerPage={TABLES_ITEMS_PER_PAGE}
              total={total}
              handleScroll={scrollToTop}
            />
          )}
        </LoaderWrapper>
      </PropertiesListWrapper>
    </Wrapper>
  );
}

const PropertiesListWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 90vh;
  box-sizing: border-box;
  padding-left: 32px;
  padding-right: 32px;
  white-space: nowrap;
`;

const RuletableStyledWrapper = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.tables.borders};
  padding-top: 1px;
  padding-bottom: 32px;
  margin-bottom: 32px;
`;

const FilterAddWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.tables.borders};
  align-items: center;
  width: 100%;
  min-height: 102px;
  padding: 8px 32px 8px 32px;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: ${(props) => props.theme.colors.white};
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.background.pageTable};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
  margin-bottom: 32px;
`;
